/**
 * Fixture Detail Book / workstation markets.
 * Keys align with Net Exposure list fixture `marketId`s.
 * Tree + bets mirror Backend-UI `net-exposure-tracker` + `bet-breakdown`.
 */

import type { BookType } from './fixture'

export type WorkstationMarketType = 'match' | 'fancy' | 'bookmaker'

export interface WorkstationOddLevel {
  price: number
  size: number
}

export interface WorkstationRunner {
  id: string
  name: string
  amountPt: number
  amountTotal: number
  /** Outer → best for back; best → outer for lay (OddsLadder order). */
  back: WorkstationOddLevel[]
  lay: WorkstationOddLevel[]
}

export interface WorkstationHorseAmount {
  runnerId: string
  amountPt: number
  amountTotal: number
}

export interface WorkstationTreeNode {
  id: string
  username: string
  level: 'agent' | 'player'
  /** Lab book.level / ROLE_LABEL — passed to getLadder as `level`. */
  accessLevel?: string
  /** Match Odds style worst-case NE (Backend-UI minNetExposure). */
  minNetExposurePt: number
  minNetExposureTotal: number
  /** Fancy NE (Backend-UI netExposure). */
  netExposurePt: number
  netExposureTotal: number
  /**
   * Lab `netexpoCommision` — CM Line Session / fancy tree column
   * (Betfair LS shown in cm-ui workstation).
   */
  netexpoCommision?: number
  horse: WorkstationHorseAmount[]
  children?: WorkstationTreeNode[]
}

export type WorkstationBetSide = 'BACK' | 'LAY'

export interface WorkstationBet {
  id: string
  userName: string
  currencyType: string
  placedAt: string
  matchedAt: string | null
  status: string
  event: string
  marketName: string
  selectionName: string
  type: WorkstationBetSide
  betRate: number
  /** Fancy / session line when the order has runs. */
  runs: number | null
  averagePriceMatched: number
  matchedAmt: number
  unMatchedAmt: number
  /** Profit / liability figure shown in Backend-UI P/L column. */
  profitLiability: number
  ip: string
}

/** Backend-UI `ip-detail-records` modal fields (IpQualityScore fixture). */
export interface WorkstationIpDetail {
  host: string
  ip: string
  country_code: string
  city: string
  region: string
  vpn: boolean
  fraud_score: number
  ISP: string
  ASN: string
  abuse_velocity: string
  active_tor: boolean
  active_vpn: boolean
  bot_status: boolean
  connection_type: string
  created_at: string
  is_crawler: boolean
  latitude: string
  longitude: string
  message: string
  mobile: boolean
  organization: string
  proxy: boolean
  recent_abuse: boolean
  request_id: string
  success: boolean
  timezone: string
  tor: boolean
  updated_at: string
}

export interface WorkstationBook {
  marketId: string
  type: WorkstationMarketType
  marketName: string
  eventName: string
  status: 'OPEN' | 'SUSPENDED' | 'CLOSED' | 'BALL_RUNNING'
  /**
   * Lab `lineLadder` / totalBooks type — UI layout stays `fancy`, but Betfair
   * line-session book + ladder `type` need this flag (not custom fancy).
   */
  lineLadder?: boolean
  runners: WorkstationRunner[]
  tree: WorkstationTreeNode
  bets: WorkstationBet[]
}

export interface WorkstationSearch {
  name: string
  event: string
  type: WorkstationMarketType
  book: BookType
}

export function parseWorkstationSearch(params: URLSearchParams): WorkstationSearch {
  const typeRaw = params.get('type')
  const type: WorkstationMarketType =
    typeRaw === 'fancy' || typeRaw === 'bookmaker' ? typeRaw : 'match'
  const bookRaw = params.get('book')
  const book: BookType = bookRaw === 'Total Book' ? 'Total Book' : 'My PT'
  return {
    name: params.get('name') ?? '',
    event: params.get('event') ?? '',
    type,
    book,
  }
}

export function marketTitleFor(type: WorkstationMarketType, marketName: string): string {
  if (type === 'match' && marketName === 'Match Odds') return 'Match Odds'
  if (type === 'fancy') return 'Session Market'
  if (type === 'bookmaker') return `Bookmaker- ${marketName}`
  return marketName || 'Market'
}

export function runnerAmountsForBook(book: WorkstationBook, bookType: BookType): number[] {
  return book.runners.map((r) => (bookType === 'Total Book' ? r.amountTotal : r.amountPt))
}

export function treeNetExposure(
  node: WorkstationTreeNode,
  type: WorkstationMarketType,
  bookType: BookType,
): number {
  if (type === 'fancy') {
    return bookType === 'Total Book' ? node.netExposureTotal : node.netExposurePt
  }
  return bookType === 'Total Book' ? node.minNetExposureTotal : node.minNetExposurePt
}

export function treeHorseAmount(
  node: WorkstationTreeNode,
  runnerId: string,
  bookType: BookType,
): number {
  const hit = node.horse.find((h) => h.runnerId === runnerId)
  if (!hit) return 0
  return bookType === 'Total Book' ? hit.amountTotal : hit.amountPt
}

function ipDetail(
  ip: string,
  partial: Partial<WorkstationIpDetail> & Pick<WorkstationIpDetail, 'city' | 'region' | 'ISP'>,
): WorkstationIpDetail {
  const now = '2026-08-03T10:00:00Z'
  return {
    host: partial.host ?? `host-${ip.replace(/\./g, '-')}.example.net`,
    ip,
    country_code: partial.country_code ?? 'IN',
    city: partial.city,
    region: partial.region,
    vpn: partial.vpn ?? false,
    fraud_score: partial.fraud_score ?? 12,
    ISP: partial.ISP,
    ASN: partial.ASN ?? 'AS24560',
    abuse_velocity: partial.abuse_velocity ?? 'none',
    active_tor: partial.active_tor ?? false,
    active_vpn: partial.active_vpn ?? false,
    bot_status: partial.bot_status ?? false,
    connection_type: partial.connection_type ?? 'Residential',
    created_at: partial.created_at ?? now,
    is_crawler: partial.is_crawler ?? false,
    latitude: partial.latitude ?? '28.6139',
    longitude: partial.longitude ?? '77.2090',
    message: partial.message ?? 'Success',
    mobile: partial.mobile ?? false,
    organization: partial.organization ?? partial.ISP,
    proxy: partial.proxy ?? false,
    recent_abuse: partial.recent_abuse ?? false,
    request_id: partial.request_id ?? `req-${ip.split('.').join('')}`,
    success: partial.success ?? true,
    timezone: partial.timezone ?? 'Asia/Kolkata',
    tor: partial.tor ?? false,
    updated_at: partial.updated_at ?? now,
  }
}

const IP_DETAILS: Record<string, WorkstationIpDetail> = {
  '103.21.44.12': ipDetail('103.21.44.12', {
    city: 'Mumbai',
    region: 'Maharashtra',
    ISP: 'Bharti Airtel',
    ASN: 'AS9498',
    fraud_score: 8,
    latitude: '19.0760',
    longitude: '72.8777',
  }),
  '49.36.12.88': ipDetail('49.36.12.88', {
    city: 'Delhi',
    region: 'Delhi',
    ISP: 'Jio',
    ASN: 'AS55836',
    fraud_score: 22,
    vpn: true,
    active_vpn: true,
    connection_type: 'Corporate',
  }),
  '122.168.9.4': ipDetail('122.168.9.4', {
    city: 'Indore',
    region: 'Madhya Pradesh',
    ISP: 'BSNL',
    ASN: 'AS9829',
    fraud_score: 55,
    recent_abuse: true,
    abuse_velocity: 'medium',
    latitude: '22.7196',
    longitude: '75.8577',
  }),
}

/** Fixture stand-in for Backend-UI `getIpDetails` / IpQualityScore. */
export function getWorkstationIpDetail(ip: string): WorkstationIpDetail | null {
  if (!ip) return null
  return IP_DETAILS[ip] ?? null
}

export function filterWorkstationBets(
  bets: WorkstationBet[],
  opts: {
    memberNames: string[]
    memberQuery: string
    currency: string
    fromDate: string | null
    toDate: string | null
  },
): WorkstationBet[] {
  const q = opts.memberQuery.trim().toLowerCase()
  const names = opts.memberNames.map((n) => n.toLowerCase()).filter(Boolean)
  const terms = [...names]
  if (q && !terms.includes(q)) terms.push(q)
  const fromMs = opts.fromDate ? Date.parse(opts.fromDate) : NaN
  const toMs = opts.toDate ? Date.parse(opts.toDate) : NaN

  return bets.filter((bet) => {
    if (opts.currency !== 'All' && bet.currencyType !== opts.currency) return false

    if (terms.length > 0) {
      const uname = bet.userName.toLowerCase()
      const hay = [
        bet.userName,
        bet.selectionName,
        bet.event,
        bet.marketName,
        bet.ip,
        bet.currencyType,
      ]
        .join(' ')
        .toLowerCase()
      if (!terms.some((t) => uname.includes(t) || hay.includes(t))) return false
    }

    const placed = Date.parse(bet.placedAt)
    if (!Number.isNaN(fromMs) && !Number.isNaN(placed) && placed < fromMs) return false
    if (!Number.isNaN(toMs) && !Number.isNaN(placed) && placed > toMs) return false
    return true
  })
}

/** Backend-UI workstation default: last 90 days → end of today. */
export function defaultBetDateRange(): { fromDate: string; toDate: string } {
  const to = new Date()
  to.setHours(23, 59, 59, 999)
  const from = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
  from.setHours(0, 0, 0, 0)
  return { fromDate: from.toISOString(), toDate: to.toISOString() }
}

/** Lab / Backend-UI display: `DD.MM.YYYY HH:mm:ss` (always 24h). */
export function toDateInputValue(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

export type LocalDateTimeParts = {
  /** `YYYY-MM-DD` for `<input type="date">` */
  date: string
  hour: number
  minute: number
  second: number
}

/** Local calendar + 24h clock parts from an ISO string. */
export function localPartsFromIso(iso: string): LocalDateTimeParts | null {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    hour: d.getHours(),
    minute: d.getMinutes(),
    second: d.getSeconds(),
  }
}

/** Build ISO from local date + 24h clock parts. */
export function isoFromLocalParts(parts: LocalDateTimeParts): string {
  const m = parts.date.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return ''
  const d = new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    parts.hour,
    parts.minute,
    parts.second,
  )
  return Number.isNaN(d.getTime()) ? '' : d.toISOString()
}

const LAB_DT =
  /^(\d{2})\.(\d{2})\.(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/

/**
 * Parse Lab `DD.MM.YYYY HH:mm:ss`, `datetime-local`, or date-only → ISO for bet APIs.
 * Missing time uses start/end of day via `endOfDay`.
 */
export function fromDateInputValue(value: string, endOfDay = false): string {
  if (!value) return ''
  const trimmed = value.trim()
  const lab = trimmed.match(LAB_DT)
  if (lab) {
    const dd = Number(lab[1])
    const mm = Number(lab[2])
    const yyyy = Number(lab[3])
    const hh = lab[4] != null ? Number(lab[4]) : endOfDay ? 23 : 0
    const mi = lab[5] != null ? Number(lab[5]) : endOfDay ? 59 : 0
    const ss = lab[6] != null ? Number(lab[6]) : endOfDay ? 59 : 0
    const d = new Date(yyyy, mm - 1, dd, hh, mi, ss)
    return Number.isNaN(d.getTime()) ? '' : d.toISOString()
  }
  const normalized = trimmed.includes('T')
    ? trimmed.length === 16
      ? `${trimmed}:00`
      : trimmed
    : `${trimmed}T${endOfDay ? '23:59:59' : '00:00:00'}`
  const d = new Date(normalized)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString()
}

export function paginateBets<T>(items: T[], page: number, pageSize: number): T[] {
  const start = Math.max(0, (page - 1) * pageSize)
  return items.slice(start, start + pageSize)
}

function levels(prices: number[], sizes: number[]): WorkstationOddLevel[] {
  return prices.map((price, i) => ({ price, size: sizes[i] ?? 0 }))
}

function horse(
  runnerId: string,
  amountPt: number,
  amountTotal: number,
): WorkstationHorseAmount {
  return { runnerId, amountPt, amountTotal }
}

function matchTree(): WorkstationTreeNode {
  return {
    id: 'root-sm',
    username: 'super_master_01',
    level: 'agent',
    minNetExposurePt: -82000,
    minNetExposureTotal: -246000,
    netExposurePt: -82000,
    netExposureTotal: -246000,
    horse: [
      horse('ind', -82000, -246000),
      horse('aus', 38000, 114000),
      horse('draw', 12000, 36000),
    ],
    children: [
      {
        id: 'master-a',
        username: 'master_alpha',
        level: 'agent',
        minNetExposurePt: -50000,
        minNetExposureTotal: -150000,
        netExposurePt: -50000,
        netExposureTotal: -150000,
        horse: [
          horse('ind', -50000, -150000),
          horse('aus', 22000, 66000),
          horse('draw', 8000, 24000),
        ],
        children: [
          {
            id: 'player-1',
            username: 'player_ravi',
            level: 'player',
            minNetExposurePt: -30000,
            minNetExposureTotal: -30000,
            netExposurePt: -30000,
            netExposureTotal: -30000,
            horse: [
              horse('ind', -30000, -30000),
              horse('aus', 12000, 12000),
              horse('draw', 5000, 5000),
            ],
          },
          {
            id: 'player-2',
            username: 'player_amit',
            level: 'player',
            minNetExposurePt: -20000,
            minNetExposureTotal: -20000,
            netExposurePt: -20000,
            netExposureTotal: -20000,
            horse: [
              horse('ind', -20000, -20000),
              horse('aus', 10000, 10000),
              horse('draw', 3000, 3000),
            ],
          },
        ],
      },
      {
        id: 'master-b',
        username: 'master_beta',
        level: 'agent',
        minNetExposurePt: -32000,
        minNetExposureTotal: -96000,
        netExposurePt: -32000,
        netExposureTotal: -96000,
        horse: [
          horse('ind', -32000, -96000),
          horse('aus', 16000, 48000),
          horse('draw', 4000, 12000),
        ],
      },
    ],
  }
}

function fancyTree(): WorkstationTreeNode {
  return {
    id: 'root-sm-f',
    username: 'super_master_01',
    level: 'agent',
    minNetExposurePt: -15500,
    minNetExposureTotal: -46500,
    netExposurePt: -15500,
    netExposureTotal: -46500,
    horse: [horse('yes', -15500, -46500), horse('no', 9000, 27000)],
    children: [
      {
        id: 'master-f',
        username: 'master_alpha',
        level: 'agent',
        minNetExposurePt: -15500,
        minNetExposureTotal: -46500,
        netExposurePt: -15500,
        netExposureTotal: -46500,
        horse: [horse('yes', -15500, -46500), horse('no', 9000, 27000)],
        children: [
          {
            id: 'player-f1',
            username: 'player_ravi',
            level: 'player',
            minNetExposurePt: -15500,
            minNetExposureTotal: -15500,
            netExposurePt: -15500,
            netExposureTotal: -15500,
            horse: [horse('yes', -15500, -15500), horse('no', 9000, 9000)],
          },
        ],
      },
    ],
  }
}

function bmTree(): WorkstationTreeNode {
  return {
    id: 'root-sm-bm',
    username: 'super_master_01',
    level: 'agent',
    minNetExposurePt: -45000,
    minNetExposureTotal: -135000,
    netExposurePt: -45000,
    netExposureTotal: -135000,
    horse: [
      horse('ind', -45000, -135000),
      horse('aus', 22000, 66000),
      horse('draw', 8000, 24000),
    ],
    children: [
      {
        id: 'master-bm',
        username: 'master_alpha',
        level: 'agent',
        minNetExposurePt: -45000,
        minNetExposureTotal: -135000,
        netExposurePt: -45000,
        netExposureTotal: -135000,
        horse: [
          horse('ind', -45000, -135000),
          horse('aus', 22000, 66000),
          horse('draw', 8000, 24000),
        ],
      },
    ],
  }
}

function matchBets(): WorkstationBet[] {
  const event = 'India v Australia'
  const market = 'Match Odds'
  return [
    {
      id: 'b1',
      userName: 'player_ravi',
      currencyType: 'INR',
      placedAt: '2026-08-03T09:12:01Z',
      matchedAt: '2026-08-03T09:12:02Z',
      status: 'EXECUTION_COMPLETE',
      event,
      marketName: market,
      selectionName: 'India',
      type: 'BACK',
      runs: null,
      betRate: 2.04,
      averagePriceMatched: 2.04,
      matchedAmt: 25000,
      unMatchedAmt: 0,
      profitLiability: 26000,
      ip: '103.21.44.12',
    },
    {
      id: 'b2',
      userName: 'player_amit',
      currencyType: 'INR',
      placedAt: '2026-08-03T09:18:44Z',
      matchedAt: '2026-08-03T09:18:45Z',
      status: 'EXECUTION_COMPLETE',
      event,
      marketName: market,
      selectionName: 'Australia',
      type: 'LAY',
      runs: null,
      betRate: 3.4,
      averagePriceMatched: 3.4,
      matchedAmt: 10000,
      unMatchedAmt: 0,
      profitLiability: -24000,
      ip: '49.36.12.88',
    },
    {
      id: 'b3',
      userName: 'player_ravi',
      currencyType: 'POINTS',
      placedAt: '2026-08-03T10:01:10Z',
      matchedAt: null,
      status: 'EXECUTABLE',
      event,
      marketName: market,
      selectionName: 'The Draw',
      type: 'BACK',
      runs: null,
      betRate: 4.6,
      averagePriceMatched: 4.6,
      matchedAmt: 0,
      unMatchedAmt: 5000,
      profitLiability: 18000,
      ip: '103.21.44.12',
    },
    {
      id: 'b4',
      userName: 'player_neha',
      currencyType: 'INR',
      placedAt: '2026-08-03T10:22:33Z',
      matchedAt: '2026-08-03T10:22:34Z',
      status: 'EXECUTION_COMPLETE',
      event,
      marketName: market,
      selectionName: 'India',
      type: 'LAY',
      runs: null,
      betRate: 2.08,
      averagePriceMatched: 2.08,
      matchedAmt: 15000,
      unMatchedAmt: 0,
      profitLiability: -16200,
      ip: '122.168.9.4',
    },
    {
      id: 'b5',
      userName: 'player_amit',
      currencyType: 'INR',
      placedAt: '2026-08-03T11:05:00Z',
      matchedAt: '2026-08-03T11:05:01Z',
      status: 'EXECUTION_COMPLETE',
      event,
      marketName: market,
      selectionName: 'Australia',
      type: 'BACK',
      runs: null,
      betRate: 3.3,
      averagePriceMatched: 3.3,
      matchedAmt: 8000,
      unMatchedAmt: 0,
      profitLiability: 18400,
      ip: '49.36.12.88',
    },
  ]
}

function fancyBets(): WorkstationBet[] {
  return [
    {
      id: 'fb1',
      userName: 'player_ravi',
      currencyType: 'INR',
      placedAt: '2026-08-03T09:40:00Z',
      matchedAt: '2026-08-03T09:40:01Z',
      status: 'EXECUTION_COMPLETE',
      event: 'India v Australia',
      marketName: 'Fancy - 6 Over Runs Ind',
      selectionName: 'Yes',
      type: 'BACK',
      betRate: 52,
      runs: 52,
      averagePriceMatched: 52,
      matchedAmt: 10000,
      unMatchedAmt: 0,
      profitLiability: -15500,
      ip: '103.21.44.12',
    },
    {
      id: 'fb2',
      userName: 'player_amit',
      currencyType: 'INR',
      placedAt: '2026-08-03T10:15:00Z',
      matchedAt: '2026-08-03T10:15:01Z',
      status: 'RT_INVALID_BET',
      event: 'India v Australia',
      marketName: 'Fancy - 6 Over Runs Ind',
      selectionName: 'No',
      type: 'LAY',
      betRate: 48,
      runs: 48,
      averagePriceMatched: 48,
      matchedAmt: 5000,
      unMatchedAmt: 0,
      profitLiability: 5000,
      ip: '49.36.12.88',
    },
  ]
}

function bmBets(): WorkstationBet[] {
  return [
    {
      id: 'bm1',
      userName: 'player_amit',
      currencyType: 'INR',
      placedAt: '2026-08-03T09:55:00Z',
      matchedAt: '2026-08-03T09:55:01Z',
      status: 'EXECUTION_COMPLETE',
      event: 'India v Australia',
      marketName: 'Bookmaker',
      selectionName: 'India',
      type: 'BACK',
      runs: null,
      betRate: 1.96,
      averagePriceMatched: 1.96,
      matchedAmt: 20000,
      unMatchedAmt: 0,
      profitLiability: 19200,
      ip: '49.36.12.88',
    },
  ]
}

/** England list fixture market — View Total / Detail Book demo (no BM on that event). */
function englandMatchTree(): WorkstationTreeNode {
  return {
    id: 'root-eng',
    username: 'super_master_01',
    level: 'agent',
    minNetExposurePt: -12000,
    minNetExposureTotal: -36000,
    netExposurePt: -12000,
    netExposureTotal: -36000,
    horse: [
      horse('eng', 28000, 84000),
      horse('nz', -41000, -123000),
      horse('draw', 6000, 18000),
    ],
    children: [
      {
        id: 'master-eng',
        username: 'master_alpha',
        level: 'agent',
        minNetExposurePt: -12000,
        minNetExposureTotal: -36000,
        netExposurePt: -12000,
        netExposureTotal: -36000,
        horse: [
          horse('eng', 28000, 84000),
          horse('nz', -41000, -123000),
          horse('draw', 6000, 18000),
        ],
      },
    ],
  }
}

function englandMatchBets(): WorkstationBet[] {
  return [
    {
      id: 'eng-b1',
      userName: 'player_ravi',
      currencyType: 'INR',
      placedAt: '2026-08-03T12:00:00Z',
      matchedAt: '2026-08-03T12:00:01Z',
      status: 'EXECUTION_COMPLETE',
      event: 'England v New Zealand',
      marketName: 'Match Odds',
      selectionName: 'New Zealand',
      type: 'BACK',
      runs: null,
      betRate: 2.1,
      averagePriceMatched: 2.1,
      matchedAmt: 20000,
      unMatchedAmt: 0,
      profitLiability: -41000,
      ip: '103.21.44.12',
    },
  ]
}

const BOOKS: Record<string, WorkstationBook> = {
  '1.98765432': {
    marketId: '1.98765432',
    type: 'match',
    marketName: 'Match Odds',
    eventName: 'England v New Zealand',
    status: 'OPEN',
    runners: [
      {
        id: 'eng',
        name: 'England',
        amountPt: 28000,
        amountTotal: 84000,
        back: levels([1.9, 1.92, 1.94], [900, 700, 500]),
        lay: levels([1.96, 1.98, 2.0], [850, 650, 450]),
      },
      {
        id: 'nz',
        name: 'New Zealand',
        amountPt: -41000,
        amountTotal: -123000,
        back: levels([3.5, 3.55, 3.6], [600, 400, 300]),
        lay: levels([3.65, 3.7, 3.75], [550, 380, 280]),
      },
      {
        id: 'draw',
        name: 'The Draw',
        amountPt: 6000,
        amountTotal: 18000,
        back: levels([4.2, 4.3, 4.4], [300, 200, 150]),
        lay: levels([4.5, 4.6, 4.7], [280, 180, 120]),
      },
    ],
    tree: englandMatchTree(),
    bets: englandMatchBets(),
  },
  '1.23456789': {
    marketId: '1.23456789',
    type: 'match',
    marketName: 'Match Odds',
    eventName: 'India v Australia',
    status: 'OPEN',
    runners: [
      {
        id: 'ind',
        name: 'India',
        amountPt: -82000,
        amountTotal: -246000,
        back: levels([2.02, 2.04, 2.06], [1200, 800, 500]),
        lay: levels([2.08, 2.1, 2.12], [900, 600, 400]),
      },
      {
        id: 'aus',
        name: 'Australia',
        amountPt: 38000,
        amountTotal: 114000,
        back: levels([3.25, 3.3, 3.35], [700, 500, 300]),
        lay: levels([3.4, 3.45, 3.5], [650, 450, 280]),
      },
      {
        id: 'draw',
        name: 'The Draw',
        amountPt: 12000,
        amountTotal: 36000,
        back: levels([4.5, 4.6, 4.7], [400, 280, 200]),
        lay: levels([4.8, 4.9, 5.0], [350, 220, 150]),
      },
    ],
    tree: matchTree(),
    bets: matchBets(),
  },
  '1.23456789-1': {
    marketId: '1.23456789-1',
    type: 'fancy',
    marketName: 'Fancy - 6 Over Runs Ind',
    eventName: 'India v Australia',
    status: 'OPEN',
    runners: [
      {
        id: 'yes',
        name: 'Yes',
        amountPt: -15500,
        amountTotal: -46500,
        back: levels([52, 51, 50], [5000, 4000, 3000]),
        lay: levels([54, 55, 56], [4800, 3800, 2800]),
      },
      {
        id: 'no',
        name: 'No',
        amountPt: 9000,
        amountTotal: 27000,
        back: levels([48, 47, 46], [4500, 3500, 2500]),
        lay: levels([50, 51, 52], [4200, 3200, 2200]),
      },
    ],
    tree: fancyTree(),
    bets: fancyBets(),
  },
  '1.23456789#bm': {
    marketId: '1.23456789#bm',
    type: 'bookmaker',
    marketName: 'Bookmaker',
    eventName: 'India v Australia',
    status: 'OPEN',
    runners: [
      {
        id: 'ind',
        name: 'India',
        amountPt: -45000,
        amountTotal: -135000,
        back: levels([1.95, 1.96, 1.97], [2000, 1500, 1000]),
        lay: levels([1.98, 1.99, 2.0], [1800, 1400, 900]),
      },
      {
        id: 'aus',
        name: 'Australia',
        amountPt: 22000,
        amountTotal: 66000,
        back: levels([3.1, 3.15, 3.2], [1100, 800, 500]),
        lay: levels([3.25, 3.3, 3.35], [1000, 700, 450]),
      },
      {
        id: 'draw',
        name: 'The Draw',
        amountPt: 8000,
        amountTotal: 24000,
        back: levels([4.2, 4.3, 4.4], [600, 400, 280]),
        lay: levels([4.5, 4.6, 4.7], [550, 350, 220]),
      },
    ],
    tree: bmTree(),
    bets: bmBets(),
  },
}

export function getWorkstationBook(marketId: string): WorkstationBook | null {
  const key = decodeURIComponent(marketId)
  if (BOOKS[key]) return BOOKS[key]!
  /* Backend-UI often uses `#bookmaker1` / `#bookmakerN` — map to fixture BM book. */
  if (key.includes('#bookmaker')) {
    return BOOKS['1.23456789#bm'] ?? null
  }
  return null
}

/**
 * Backend-UI `getUrl` for View Total:
 * matchOdds.marketId + exchange BM marketIds (`bookMakerforNetExposure` / `#`), comma-joined.
 * Fancy and PB are excluded.
 */
export function advanceMarketIdsFromEvent(event: {
  matchOdds?: { marketId: string } | null
  data: { marketId: string; bookMakerforNetExposure?: boolean }[]
}): string[] {
  const ids: string[] = []
  if (event.matchOdds?.marketId) ids.push(event.matchOdds.marketId)
  for (const row of event.data) {
    if (row.bookMakerforNetExposure || row.marketId.includes('#')) {
      if (!ids.includes(row.marketId)) ids.push(row.marketId)
    }
  }
  return ids
}

/** Parse advance route `:id` (`a,b#bm` or encoded). */
export function parseAdvanceMarketIds(raw: string): string[] {
  const decoded = decodeURIComponent(raw)
  return decoded
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

export function advanceWorkstationPath(marketIds: string[]): string {
  return `/book-management/net-exposure/advance-workstation/${encodeURIComponent(marketIds.join(','))}`
}

/** Build list → workstation URL search string. */
export function workstationSearchFromMarket(input: {
  marketName: string
  eventName: string
  type: WorkstationMarketType
  book: BookType
}): string {
  const q = new URLSearchParams({
    name: input.marketName,
    event: input.eventName,
    type: input.type,
    book: input.book,
  })
  return q.toString()
}
